import { useContextProvider } from "./../context/providers/ContextProvider";
import { useState } from "react";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import {
  getBalance,
  getBilledPeriods,
  getHeadAccountBillDetails,
  getCostsBreakdown,
  getLastBill,
  getOverdue,
  getStatements,
  getSubAccountBillDetails,
  getUnbilledCosts,
  getUnbilledTotals,
} from "./api";
import { AjaxResponse } from "../_app/api";
import { serviceAnalysisFilters, usageAnalysisFilters } from "./filterConfig";
import { Filter, Query } from "../filter/types";
import { GetStatementsResponse } from "./types";
import { addFormatFunctionToFilters } from "../filter/utils";

export function useUnbilledTotals(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["unbilled-totals", { accountId }],
    queryFn: () => getUnbilledTotals(accountId),
    ...options,
  });
}

export function useLastBill(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["billing-last", { accountId }],
    queryFn: () => getLastBill(accountId),
    ...options,
  });
}

export function useCostsBreakdown(period: string, systemInvoiceRef: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["costs-breakdown", { accountId, period, systemInvoiceRef }],
    queryFn: () => getCostsBreakdown(accountId, period, systemInvoiceRef),
    ...options,
  });
}

export function useBillDetails(periods: string[], isHeadAccount: boolean = false, options = {}) {
  const { contextId: accountId } = useContextProvider();
  const queryFn = isHeadAccount ? getHeadAccountBillDetails : getSubAccountBillDetails;
  const queries = periods
    .map((p, i) => {
      return {
        queryKey: ["billDetails", { accountId, p, isHeadAccount }],
        queryFn: () => queryFn(accountId, p),
        ...options,
      };
    })
    .filter((q) => q.queryKey);
  return useQueries({ queries });
}

export function useBilledPeriods(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["periods", { accountId }],
    queryFn: () => getBilledPeriods(accountId),
    ...options,
  });
}

export function useUnbilledCosts(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["unbilled-costs", accountId],
    queryFn: () => getUnbilledCosts(accountId),
    ...options,
  });
}

export function useGetBalance(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["balance", { accountId }],
    queryFn: () => getBalance(accountId),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useGetOverdue(options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["overdue", { accountId }],
    queryFn: () => getOverdue(accountId),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useStatements(limit: number, queries: Query[], options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<GetStatementsResponse, AjaxResponse>({
    queryKey: ["statements", { accountId, limit, queries }],
    queryFn: ({ pageParam }) => getStatements(accountId, pageParam as string, limit, queries),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: any) => {
      return lastPage?.nextPageToken || undefined;
    },
    retry: 2,
    ...options,
  });
}

export function useUsageAnalysisFilters() {
  const { contextId: accountId } = useContextProvider();
  const [data, setData] = useState<Filter[]>([]);
  const filters = usageAnalysisFilters;
  const formatData = async () => {
    try {
      const response = await addFormatFunctionToFilters(filters, accountId);
      setData(response);
    } catch (error) {
      console.error("formatting failed");
    }
  };
  if (!data.length) formatData();
  return { data };
}

export function useServiceAnalysisFilters() {
  const { contextId: accountId } = useContextProvider();
  const [data, setData] = useState<Filter[]>([]);
  const filters = serviceAnalysisFilters;
  const formatData = async () => {
    try {
      const response = await addFormatFunctionToFilters(filters, accountId);
      setData(response);
    } catch (error) {
      console.error("formatting failed");
    }
  };
  if (!data.length) formatData();
  return { data };
}
